import {
    Action,
    AnyAction,
    combineReducers,
    configureStore,
    createListenerMiddleware,
    Middleware,
    Reducer,
    ThunkAction,
    ThunkDispatch,
} from '@reduxjs/toolkit';
import {
    inventorySplitApi as inventoryApi,
    merchantAdminSplitApi as merchantAdminApi,
    scanSplitApi as scanApi,
} from '@services/apiBase';
import type { MerchantsState } from '@features/merchants/merchantsSlice';
import type { ScansState } from '@features/scans/scanSlice';
import type { ProductsState } from '@features/products/productsSlice';
import type { InventoryState } from '@features/inventory/inventorySlice';
import authReducer from '../features/common/auth/authSlice';
import rtkQueryErrorLogger from './rtk-query-error-logger';

export const staticReducers = {
    auth: authReducer,
    [inventoryApi.reducerPath]: inventoryApi.reducer,
    [merchantAdminApi.reducerPath]: merchantAdminApi.reducer,
    [scanApi.reducerPath]: scanApi.reducer,
};

const regionListenerMiddleware = createListenerMiddleware<RootState>();
regionListenerMiddleware.startListening({
    predicate: (action: any, currentState: { auth: { currentRegion: any; }; }, previousState: {
        auth: { currentRegion: any; };
    }) => currentState.auth.currentRegion !== previousState.auth.currentRegion,
    effect: async (action: any, listenerApi: {
        dispatch: (arg0: any) => void;
    }) => {
        listenerApi.dispatch(scanApi.util.invalidateTags(['Scans']));
    },
});

const middlewares: Middleware[] = [
    regionListenerMiddleware.middleware,
    rtkQueryErrorLogger,
    inventoryApi.middleware,
    merchantAdminApi.middleware,
    scanApi.middleware,
];

export const store = configureStore({
    reducer: {
        ...staticReducers,
    },
    middleware: (getDefaultMiddleware) => getDefaultMiddleware()
        .concat(middlewares),
});

export type AppStore = typeof store;

// TODO: ...will need to add reducer code splitting later...
const asyncReducers: { [key: string]: Reducer } = {};

// eslint-disable-next-line no-unused-vars
export const registerReducer: (name: string, reducer: Reducer) => AppStore = (name, reducer) => {
    if (!asyncReducers[name]) {
        asyncReducers[name] = reducer;
        store.replaceReducer(combineReducers({ ...staticReducers, ...asyncReducers }));
    }
    return store;
};

export type AppDispatch = ThunkDispatch<RootState, undefined, AnyAction>;
export type RootState = ReturnType<typeof store.getState> & {
    merchants: MerchantsState;
    scans: ScansState;
    products: ProductsState;
    inventory: InventoryState;
};
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType,
    RootState,
    unknown,
    Action<string>>;
